import dayjs from "dayjs";
import Parse from "parse";
import { Stop, Stop_Times } from "../../parsegenerated";

export class StopConverter {
  private offer: any;
  private offerRoute: any;
  private places: any[];
  private coords: Parse.GeoPoint[] = [];
  private orderedPlaces: any[] = [];
  private errorPlaces: any[] = [];

  private stops: Stop[] = [];
  private stopTimes: Stop_Times[] = [];

  constructor(offer: any, places: any[]) {
    this.offer = offer;
    this.offerRoute = offer.get("route") as any;
    this.places = places;
    this.init();
  }

  getStops() {
    return this.stops;
  }

  getStopTimes() {
    return this.stopTimes;
  }

  private init() {
    this.convertRouteToCoords();
    this.sortPlaces();
    this.createStops();
    this.createStopTimes();
  }

  /**
   * Convert the route to an array of coordinates stored in the coords array
   */
  private convertRouteToCoords() {
    const extractedCoords = (this.offerRoute.get("routeFull") as any).points
      .coordinates as [number, number][];

    this.coords = extractedCoords.map(
      (coord) => new Parse.GeoPoint(coord[1], coord[0])
    );
  }

  /**
   * Sort the places according to the order of the coordinates in the route.
   * The ordered places are stored in the orderedPlaces array.
   */
  private sortPlaces() {
    this.coords.forEach((coord) => {
      const closestPlace = this.getClosestPlace(coord);
      this.orderedPlaces.push(closestPlace);
    });

    // remove consecutive duplicates
    this.orderedPlaces = this.orderedPlaces.filter(
      (item, index, array) => index === 0 || item !== array[index - 1]
    );

    this.findPotentialErrors();
  }

  /**
   * Create the stops for the GTFS file
   */
  private createStops() {
    this.orderedPlaces.forEach((place, index) => {
      const stop = new Stop({
        stop_id: place.id,
        stop_name: place.get("name"),
        stop_lat: place.get("location").latitude.toString(),
        stop_lon: place.get("location").longitude.toString(),
      });

      this.stops.push(stop);
    });
  }

  /**
   * Create the stop times for the GTFS file
   */
  private createStopTimes() {
    const waitingTime = 60000; // 1 minute
    const totalTime = this.offerRoute.get("routeFull").time;
    const totalDistance = this.getRouteDistance();

    let currentTime = this.getStartTime(); // track the current time in ms
    // loop through all stops except the last one
    for (let i = 0; i < this.stops.length - 1; i++) {
      const arrivalTime = new Date(currentTime);
      currentTime += i === 0 ? 0 : waitingTime; // add the waiting time for all stops except the first one
      const departureTime = new Date(currentTime); // 1 minute later
      const stopTime = new Stop_Times({
        arrival_time: arrivalTime,
        departure_time: departureTime,
        stop_id: this.stops[i],
        stop_sequence: i,
      });
      this.stopTimes.push(stopTime);

      // finding the distance between the current stop and the next stop
      const distance = this.getDistanceComplex(
        new Parse.GeoPoint(
          Number.parseFloat(this.stops[i].get("stop_lat") ?? "0"),
          Number.parseFloat(this.stops[i].get("stop_lon") ?? "0")
        ),
        new Parse.GeoPoint(
          Number.parseFloat(this.stops[i + 1].get("stop_lat") ?? "0"),
          Number.parseFloat(this.stops[i + 1].get("stop_lon") ?? "0")
        )
      );
      // calculate the travel time based on the distance to the next stop
      const travelTime = (distance / totalDistance) * totalTime;

      currentTime += travelTime;
    }
    // handle last stop
    const arrivalTime = new Date(currentTime);
    const departureTime = new Date(currentTime);
    const stopTime = new Stop_Times({
      arrival_time: arrivalTime,
      departure_time: departureTime,
      stop_id: this.stops[this.stops.length - 1],
      stop_sequence: this.stops.length - 1,
    });

    this.stopTimes.push(stopTime);
  }

  private getStartTime() {
    const offerTime = this.offer.get("time");
    const [hours, minutes] = offerTime
      .split(":")
      .map((time) => Number.parseInt(time));

    if (!hours || !minutes) {
      return 0;
    }

    return dayjs(0)
      .add(hours, "hour")
      .add(minutes, "minute")
      .toDate()
      .getTime();
  }

  /**
   * Calculate the route total distance
   */
  private getRouteDistance() {
    let distance = 0;
    for (let i = 0; i < this.stops.length - 1; i++) {
      distance += this.getDistanceComplex(
        new Parse.GeoPoint(
          Number.parseFloat(this.stops[i].get("stop_lat") ?? "0"),
          Number.parseFloat(this.stops[i].get("stop_lon") ?? "0")
        ),
        new Parse.GeoPoint(
          Number.parseFloat(this.stops[i + 1].get("stop_lat") ?? "0"),
          Number.parseFloat(this.stops[i + 1].get("stop_lon") ?? "0")
        )
      );
    }
    return distance;
  }

  /**
   * Find potential errors in the route, stored in the errorPlaces array
   */
  private findPotentialErrors() {
    this.findDuplicates();
    // Other error checks go here
    // ...
  }

  /**
   * Find duplicates in the orderedPlaces array and store them in the errorPlaces array
   */
  private findDuplicates() {
    const duplicates = this.orderedPlaces.filter((place, index, array) => {
      return array.indexOf(place) !== index;
    });

    this.errorPlaces.push(...duplicates);
  }

  /**
   * Find the closest place to a given coordinate
   * @param coord
   * @returns the closest place
   */
  private getClosestPlace(coord: Parse.GeoPoint) {
    const closest = this.places.reduce((prev, curr) => {
      // const prevDist = this.getDistanceComplex(prev.get("location"), coord);
      // const currDist = this.getDistanceComplex(curr.get("location"), coord);

      // simple distance calculation
      const currDist = coord.kilometersTo(curr.get("location"));
      const prevDist = coord.kilometersTo(prev.get("location"));
      return prevDist < currDist ? prev : curr;
    });
    return closest;
  }

  /**
   * Calculate the distance between two coordinates using the Haversine formula
   * @param coord1
   * @param coord2
   * @returns distance in meters
   */
  private getDistanceComplex(coord1: Parse.GeoPoint, coord2: Parse.GeoPoint) {
    const R = 6371e3; // metres
    const φ1 = (coord1.latitude * Math.PI) / 180; // φ, λ in radians
    const φ2 = (coord2.latitude * Math.PI) / 180;
    const Δφ = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
    const Δλ = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // in metres
  }
}
